// World persistence via IndexedDB. Stores only player-modified chunks (as raw
// block arrays) plus the seed, time and player state, so a world regenerates
// from its seed and then overlays the saved edits. Host-side (uses IndexedDB).

export interface SavedSlot {
  item: number;
  count: number;
}

export interface SavedWorld {
  name: string;
  seed: number;
  time: number;
  player: {
    x: number;
    y: number;
    z: number;
    yaw: number;
    pitch: number;
    health: number;
    selected: number;
    slots: (SavedSlot | null)[];
  };
  /** Modified chunks keyed by "cx,cz" → raw block array. */
  chunks: Record<string, Uint8Array>;
  savedAt: number;
}

export interface WorldMeta {
  name: string;
  seed: number;
  savedAt: number;
}

const DB_NAME = 'blockfall';
const STORE = 'worlds';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'name' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const store = db.transaction(STORE, mode).objectStore(STORE);
        const req = fn(store);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export function saveWorld(world: SavedWorld): Promise<void> {
  return tx('readwrite', (store) => store.put(world)).then(() => undefined);
}

export function loadWorld(name: string): Promise<SavedWorld | null> {
  return tx<SavedWorld | undefined>('readonly', (store) => store.get(name)).then((r) => r ?? null);
}

export function deleteWorld(name: string): Promise<void> {
  return tx('readwrite', (store) => store.delete(name)).then(() => undefined);
}

export function listWorlds(): Promise<WorldMeta[]> {
  return tx<SavedWorld[]>('readonly', (store) => store.getAll()).then((worlds) =>
    worlds
      .map((w) => ({ name: w.name, seed: w.seed, savedAt: w.savedAt }))
      .sort((a, b) => b.savedAt - a.savedAt),
  );
}
